import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import LabelParameters from "../LabelParameters";
import styles from "../../../screens/ChatView/ChatView.module.css";
import Button from "../../Button/Button";
import {ReactComponent as PlayIcon} from "../../../assets/PlayIcon.svg";
import { useTranslation } from "react-i18next";

const VoiceRecorderWave = forwardRef(({ onSend, onCancel,setParameterData }, ref) => {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [samples, setSamples] = useState([]); // Array de amplitudes cada 0.25s
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef([]);
  const animationRef = useRef(null);
  const [visualSamples, setVisualSamples] = useState([]); // Para visualización en tiempo real
  const [recordingTime, setRecordingTime] = useState(0);
  const [hoverBar, setHoverBar] = useState(null); // <-- Nuevo estado para hover
  const recordingRef = useRef(recording);
  const playingRef = useRef(playing);
  const audioUrlRef = useRef(audioUrl);

  // Iniciar grabación
  const startRecording = async () => {
    setSamples([]);
    setVisualSamples([]);
    setAudioUrl(null);
    setAudioBlob(null);
    setAudioChunks([]);
    setCurrentTime(0);
    setDuration(0);
    setPlaying(false);
    setRecordingTime(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new window.MediaRecorder(stream);
      setMediaRecorder(mediaRecorder);
      let localChunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) localChunks.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(localChunks, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setDuration(blob.duration); // Se actualizará al cargar el audio
      };
      // Web Audio API para analizar amplitud
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
      let lastSampleTime = 0;
      let tempSamples = [];
      let tempMax = 0;
      let startTimestamp = Date.now();
      // Animación para capturar amplitud cada 0.25s
      const capture = () => {
        analyser.getByteTimeDomainData(dataArrayRef.current);
        // Calcular amplitud máxima en la ventana
        const max = Math.max(
          ...dataArrayRef.current.map((v) => Math.abs(v - 128))
        );
        // Escalar la amplitud para que sea más visible (tipo WhatsApp)
        const scaled = Math.min(1, max / 64); // 0-1, 64 es un valor empírico para "exagerar"
        tempMax = Math.max(tempMax, scaled);
        const now = Date.now();
        if (now - lastSampleTime >= 250) {
          // 0.25s
          tempSamples.push(tempMax);
          setVisualSamples([...tempSamples]);
          tempMax = 0;
          lastSampleTime = now;
        }
        if (mediaRecorder.state === "recording") {
          setRecordingTime((now - startTimestamp) / 1000);
          animationRef.current = requestAnimationFrame(capture);
        } else {
          setSamples([...tempSamples]);
        }
      };
      mediaRecorder.onstart = () => {
        setRecording(true);
        animationRef.current = requestAnimationFrame(capture);
      };
      mediaRecorder.onstop = () => {
        setRecording(false);
        cancelAnimationFrame(animationRef.current);
        setSamples([...tempSamples]);
        setVisualSamples([...tempSamples]);
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(localChunks, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };
      mediaRecorder.start();
    } catch (err) {
      alert("No se pudo acceder al micrófono");
      setRecording(false);
    }
  };

  // Detener grabación
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
    }
  };

  // Borrar grabación
  const handleCancel = () => {
    setAudioUrl(null);
    setAudioBlob(null);
    setSamples([]);
    setVisualSamples([]);
    setAudioChunks([]);
    setRecording(false);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (onCancel) onCancel();
  };

  // Reproducir audio
  const handlePlay = () => {
    if (!audioUrl) return;
    setPlaying(true);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  };
  const handlePause = () => {
    setPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };
    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    const onLoadedMetadata = () => {
      // Fix para blobs: si la duración es Infinity o 0, forzar seek para obtener la duración real
      if (!isFinite(audio.duration) || audio.duration === 0) {
        const fixDuration = () => {
          if (isFinite(audio.duration) && audio.duration > 0) {
            setDuration(audio.duration);
            audio.removeEventListener("timeupdate", fixDuration);
            audio.currentTime = 0; // Opcional: volver al inicio
          }
        };
        audio.addEventListener("timeupdate", fixDuration);
        audio.currentTime = 1e101;
      } else {
        setDuration(audio.duration);
      }
    };
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    return () => {
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
  }, [audioUrl]);

  // Visualización de barras
  const bars = audioUrl ? samples : visualSamples;
  // Barra activa: calcula el índice de barra según currentTime y duración
  let currentBar = 0;
  if (audioUrl && duration > 0 && bars.length > 0) {
    currentBar = Math.floor((currentTime / duration) * bars.length);
    if (currentBar < 0) currentBar = 0;
    if (currentBar > bars.length - 1) currentBar = bars.length - 1;
  }

  // Fuerza re-render en cada frame de reproducción para animar la onda
  useEffect(() => {
    let raf;
    if (playing) {
      const update = () => {
        setCurrentTime(audioRef.current.currentTime);
        raf = requestAnimationFrame(update);
      };
      raf = requestAnimationFrame(update);
    }
    return () => raf && cancelAnimationFrame(raf);
  }, [playing]);

  // Click en barra (scrubbing)
  const handleBarClick = (i) => {
    if (!audioRef.current || !duration || !audioUrl) return;
    const newTime = (i / bars.length) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    // Si está en pausa, fuerza un re-render para actualizar la onda
    if (!playing) {
      setTimeout(() => setCurrentTime(audioRef.current.currentTime), 10);
    } else {
      setPlaying(true);
      audioRef.current.play();
    }
  };

  // Formatear segundos a mm:ss
  const formatTime = (s) => {
    if (!isFinite(s) || isNaN(s) || s < 0) return "00:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    startRecording();
  }, []);
  const audioBlobRef = useRef(null);
  const samplesRef = useRef([]);

  const audioBlobReadyRef = useRef(null);

  useEffect(() => {
    if (audioBlob && audioBlobReadyRef.current) {
      audioBlobReadyRef.current(audioBlob);
      audioBlobReadyRef.current = null;
    }
  }, [audioBlob]);
  useEffect(() => {
    audioBlobRef.current = audioBlob;
    samplesRef.current = samples;
  }, [audioBlob, samples]);

  useEffect(() => {
    recordingRef.current = recording;
  }, [recording]);

  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  useEffect(() => {
    audioUrlRef.current = audioUrl;
  }, [audioUrl]);
  // Exponer métodos con useImperativeHandle
  useImperativeHandle(ref, () => ({
    startRecording,
    stopRecording,
    handlePlay,
    handlePause,
    handleCancel,
    sendRecording: async () => {
      // Si ya existe el blob, enviarlo de inmediato
      if (audioBlobRef.current) {
        onSend &&
          onSend({
            audioBlob: audioBlobRef.current,
            samples: samplesRef.current,
          });
        return;
      }

      // Si no, esperar a que se genere
      await new Promise((resolve) => {
        audioBlobReadyRef.current = resolve;
      });

      onSend &&
        onSend({
          audioBlob: audioBlobRef.current,
          samples: samplesRef.current,
        });
    },

    isRecording: () => recordingRef.current,
    isPlaying: () => playingRef.current,
    hasAudio: () => !!audioUrlRef.current,
  }));
  return (
    <div className={styles.chatRecorder}>
      <div className={styles.chatRecorderPlay}>
        {/* Play/Pause */}
        {/* {audioUrl && (
            playing ? (
              <button onClick={handlePause} title="Pausar">⏸️</button>
            ) : (
              <button onClick={handlePlay} title="Reproducir">▶️</button>
            )
          )}
          
          {!audioUrl && !recording && (
            <button onClick={startRecording} title="Grabar">🎤</button>
          )}
          {recording && (
            <button onClick={stopRecording} title="Detener">⏹️</button>
          )}
          {(audioUrl || recording) && (
            <button onClick={handleCancel} title="Borrar">🗑️</button>
          )}
          {audioUrl && (
            <button
              onClick={() =>{
                onSend && onSend({ audioBlob, samples })
              }}
              title="Enviar"
              className={styles.chatRecorderPlayButton}
            >
              📤
            </button>
          )} */}
        {/* Duración */}
        {/* {audioUrl && (
            <span style={{ fontSize: 12, color: "#888" }}>
              {formatTime(duration)}
            </span>
          )} */}
        <audio
          ref={audioRef}
          src={audioUrl || undefined}
          style={{ display: "none" }}
        />
      </div>
      {/* Barras de onda */}
      <div className={styles.chatRecorderBars}>
        {bars.map((amp, i) => {
          let color = "#bbb";
          // Marcar de verde desde el inicio hasta la barra actual (incluida)
          if (audioUrl && duration > 0 && i <= currentBar)
            color = "rgb(16 163 127)";
          if (hoverBar === i) color = "#81c784";
          return (
            <div
              key={i}
              style={{
                width: 6,
                height: Math.max(amp * 40, 4),
                background: color,
                borderRadius: 2,
                transition: "background 0.1s, height 0.1s",
              }}
              onClick={() => handleBarClick(i)}
              onMouseEnter={() => setHoverBar(i)}
              onMouseLeave={() => setHoverBar(null)}
            />
          );
        })}
      </div>
      {/* Mostrar segundos grabados o duración total */}
      <div className={styles.chatRecorderBarsBar}>
        {recording && !audioUrl && <span>{formatTime(recordingTime)}</span>}
        {/* {!recording && audioUrl && (
            <span>
              Duración: {formatTime(isFinite(duration) && !isNaN(duration) && duration > 0 ? duration : (audioRef.current && !isNaN(audioRef.current.duration) && isFinite(audioRef.current.duration) && audioRef.current.duration > 0 ? audioRef.current.duration : 0))}
            </span>
          )} */}
        {audioUrl && (
          <span style={{ marginLeft: 8 }}>
            {formatTime(currentTime)} /{" "}
            {formatTime(
              isFinite(duration) && !isNaN(duration) && duration > 0
                ? duration
                : audioRef.current &&
                    !isNaN(audioRef.current.duration) &&
                    isFinite(audioRef.current.duration) &&
                    audioRef.current.duration > 0
                  ? audioRef.current.duration
                  : 0
            )}
          </span>
        )}
      </div>
    </div>
  );
});


const handleSendVoice = ({ audioBlob, samples }) => {
    // setShowVoiceRecorder(false);

    // Convertir Blob a base64 correctamente
    const reader = new FileReader();
    reader.onload = () => {
      // Obtener solo la parte base64 del data URL (remover "data:audio/webm;base64,")
      const base64String = reader.result.split(',')[1];

      if (!base64String) {
        console.error('Failed to convert audio to base64');
        return;
      }

    };

    reader.onerror = (error) => {
      console.error('Error converting audio to base64:', error);
    };
    // Leer el Blob como data URL (que incluye base64)
    reader.readAsDataURL(audioBlob);
  };


const VoiceRecorder = ({
  parameterData,
  setParameterData,
  handleChange,
  editingInput,
  setEditingInput,
}) => {
  const recorderRef = useRef();
  const [t] = useTranslation("Contacts");

  const [hasStoppedRecording, setHasStoppedRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!recorderRef.current) return;
      setHasStoppedRecording(!recorderRef.current.isRecording());
      setIsPlaying(recorderRef.current.isPlaying());
      setIsRecording(recorderRef.current.isRecording());
    }, 200); // cada 200ms o el intervalo que te parezca bien

    return () => clearInterval(interval);
  }, []);
  return (
    <div>
      <LabelParameters
        value={parameterData.voiceRecorder}
        text={"voiceRecorder"}
        editingInput={editingInput}
        setEditingInput={setEditingInput}
      >
     <div className={styles.voiceRecorderContainer}>
     {!showVoiceRecorder && (
            <div onClick={() => setShowVoiceRecorder(true)}><PlayIcon /></div>
        )}
        {showVoiceRecorder && (

            <VoiceRecorderWave
          onSend={handleSendVoice}
          onCancel={() => {}}
          ref={recorderRef}
          setParameterData={setParameterData}
        />
        )}
        {showVoiceRecorder && (
          <>
            {!hasStoppedRecording && (
              <button
                onClick={() => {
                  recorderRef.current?.stopRecording();
                }}
                data-tooltip={t("stop")}
              >
                ⏹️
              </button>
            )}

            {hasStoppedRecording &&
              (isPlaying ? (
                <button
                  onClick={() => {
                    recorderRef.current?.handlePause();
                  }}
                  data-tooltip={t("pause")}
                >
                  ⏸️{" "}
                </button>
              ) : (
                <button
                  onClick={() => {
                    recorderRef.current?.handlePlay();
                  }}
                  data-tooltip={t("play")}
                >
                  ▶️{" "}
                </button>
              ))}

            <button
              onClick={() => {
                recorderRef.current?.handleCancel();
              }}
              data-tooltip={t("delete")}
            >
              🗑️{" "}
            </button>

            {/* {hasStoppedRecording && (
              <button
                onClick={async () => {
                  const recorder = recorderRef.current;
                  if (!recorder) return;

                  if (isRecording) {
                    recorder.stopRecording();
                  }

                  await recorder.sendRecording();
                }}
                data-tooltip={t("send")}
              >
                📤
              </button>
            )} */}
          </>
        )}

        {hasStoppedRecording && (            
            <Button
            type="white"
            headerStyle={{ borderRadius: "999px" }}
            action={async () => {
            const recorder = recorderRef.current;
            if (!recorder) return;
            
            if (isRecording) {
                recorder.stopRecording();
            }
            
            await recorder.sendRecording();
        }}
        >
          {t("transcribe")}
        </Button>
        )}
     </div>
      </LabelParameters>
    </div>
  );
};

export default VoiceRecorder;
